import { Link } from '@inertiajs/react';
import { useMemo, useState } from 'react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';
import { cn } from '../../../lib/cn';

const OBSERVATION_BADGE = {
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  correct_usage: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

function MasteryBar({ level }) {
  const pct = Math.round(level * 100);
  const colorClass =
    pct >= 75 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full', colorClass)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right tabular-nums">
        {pct}%
      </span>
    </div>
  );
}

function MasteriesTable({ masteries }) {
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    if (filter === 'all') return masteries;
    if (filter === 'weak') return masteries.filter((m) => m.mastery_level < 0.4);
    if (filter === 'developing')
      return masteries.filter((m) => m.mastery_level >= 0.4 && m.mastery_level < 0.75);
    if (filter === 'mastered') return masteries.filter((m) => m.mastery_level >= 0.75);
    return masteries;
  }, [masteries, filter]);

  if (masteries.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Concept mastery</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          No grammar mastery records yet.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Concept mastery
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            ({filtered.length} of {masteries.length})
          </span>
        </h3>
        <div className="flex items-center gap-1 text-xs">
          {['all', 'weak', 'developing', 'mastered'].map((b) => (
            <button
              key={b}
              onClick={() => setFilter(b)}
              className={cn(
                'px-2 py-1 rounded',
                filter === b
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              )}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            <tr className="border-b border-gray-100 dark:border-gray-800">
              <th className="py-2 pr-3 font-medium">Concept</th>
              <th className="py-2 pr-3 font-medium">Mastery</th>
              <th className="py-2 pr-3 font-medium text-right">Correct</th>
              <th className="py-2 pr-3 font-medium text-right">Attempts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((m) => (
              <tr key={m.concept_id}>
                <td className="py-2 pr-3 text-gray-900 dark:text-white">{m.label}</td>
                <td className="py-2 pr-3">
                  <MasteryBar level={m.mastery_level} />
                </td>
                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                  {m.correct}
                </td>
                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                  {m.attempts}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ObservationsList({ observations }) {
  if (observations.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recent observations</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">No observations yet.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
        Recent observations
        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
          (latest {observations.length})
        </span>
      </h3>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {observations.map((o) => (
          <div key={o.id} className="py-3">
            <div className="flex items-center gap-2 mb-1">
              <span
                className={cn(
                  'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full',
                  OBSERVATION_BADGE[o.observation_type] ||
                    'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                )}
              >
                {o.observation_type.replace('_', ' ')}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{o.concept_label}</span>
              {!o.teacher_confirmed && (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  Pending
                </span>
              )}
            </div>
            {o.excerpt && (
              <p className="text-sm text-gray-700 dark:text-gray-300 italic">"{o.excerpt}"</p>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function UserGrammar({ student, grammar_summary, masteries, observations }) {
  return (
    <TeacherShell scrollTitle={<>{student.full_name}  ›  Grammar</>}>
      <TeacherPageHeader
        title={`${student.full_name} — Grammar`}
        subtitle={student.email}
        backLink={`/teacher/users/${student.id}`}
        backLabel="Back to profile"
        actions={
          <div className="flex items-center gap-2">
            <Link href={`/teacher/users/${student.id}/vocabulary`}>
              <Button variant="ghost">Vocabulary →</Button>
            </Link>
            <Link href={`/teacher/concepts/grammar?student_id=${student.id}`}>
              <Button variant="secondary">Knowledge graph</Button>
            </Link>
          </div>
        }
      />

      <div className="max-w-4xl space-y-6">
        {grammar_summary && (
          <Card className="p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Overview</h3>
            <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400">
              <span><strong className="text-gray-900 dark:text-white">{grammar_summary.mastery_overview.total}</strong> assessed</span>
              <span><strong className="text-gray-900 dark:text-white">{grammar_summary.mastery_overview.mastered}</strong> mastered</span>
              <span><strong className="text-gray-900 dark:text-white">{grammar_summary.mastery_overview.developing}</strong> developing</span>
              <span><strong className="text-gray-900 dark:text-white">{grammar_summary.mastery_overview.weak}</strong> weak</span>
              <span><strong className="text-gray-900 dark:text-white">{grammar_summary.observation_counts.total}</strong> observations ({grammar_summary.observation_counts.errors} errors)</span>
            </div>
          </Card>
        )}

        <MasteriesTable masteries={masteries || []} />
        <ObservationsList observations={observations || []} />
      </div>
    </TeacherShell>
  );
}
